
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { ServerStateKeysEnum } from '@/common/apis/serverStateKeysEnum';
import { useStat } from '@/common/apis/services/search/stat';
import Skeleton from '@/common/components/atom/skeleton/skeleton';
import Text from '@/common/components/atom/text';
import { LayoutWithHeaderAndFooter } from '@/common/components/layouts/layoutWithHeaderAndFooter';
import Seo from '@/common/components/layouts/seo';
import { withCSR } from '@/common/hoc/withCsr';
import { withServerUtils } from '@/common/hoc/withServerUtils';
import useCustomize, { ThemeConfig } from '@/common/hooks/useCustomize';
import useResponsive from '@/common/hooks/useResponsive';
import { splunkInstance } from '@/common/services/splunk';

import { removeHtmlTagInString } from '@/common/utils/removeHtmlTagInString';
import { hasDoctorIdentity } from '@/common/utils/doctorIdentity';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import MobileToolbar from '@/modules/search/components/filters/mobileToolbar';
import MobileRowFilter from '@/modules/search/components/filters/rowFilter';
import UnknownCity from '@/modules/search/components/unknownCity';
import { useSearch } from '@/modules/search/hooks/useSearch';
import { useSearchRouting } from '@/modules/search/hooks/useSearchRouting';
import { useSearchStore } from '@/modules/search/store/search';
import Filter from '@/modules/search/view/filter';
import { Result } from '@/modules/search/view/result';
import SearchSeoBox from '@/modules/search/view/seoBox';
import Suggestion from '@/modules/search/view/suggestion';
import { preloadSearchBox } from '@/modules/search/view/suggestion/suggestion';
import { loadResultsCode } from '@/modules/search/functions/resultsCode';
import { useShowPlasmicSuggestion } from '@/modules/search/hooks/useShowPlasmicSuggestion';
import buildListingSeoExperiment, { listingJsonLds } from '@/modules/search/functions/buildListingSeoExperiment';
import { useFeatureValue, useFeatureIsOn, GrowthBook } from '@growthbook/growthbook-react';
import { addCommas } from '@persian-tools/persian-tools';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import axios from 'axios';
import { GetServerSideProps, GetServerSidePropsContext, NextApiRequest } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { growthbook } from '../_app';
import { Fragment } from '@/common/fragment';
import { useFilterChange } from '@/modules/search/hooks/useFilterChange';
import classNames from '@/common/utils/classNames';
import getConfig from 'next/config';
const Sort = dynamic(() => import('@/modules/search/components/filters/sort'), {
  loading: () => <Skeleton w="100%" h="3rem" />,
  ssr: false,
});
const { publicRuntimeConfig, serverRuntimeConfig } = getConfig();
import SearchGlobalContextsProvider from '../../../.plasmic/plasmic/paziresh_24_search/PlasmicGlobalContextsProvider';
import { isResultClick, markSearchResultClick } from '@/modules/chat/yara/symptomInvite';
// Yara is offered only under symptom searches (owner 2026-09-27); client-only.
const YaraSymptomInvite = dynamic(() => import('@/modules/chat/yara/yaraSymptomInvite'), { ssr: false });
import { applySearchUnavailableStatus, isSearchDegraded } from '@/modules/search/functions/searchUnavailableStatus';
import {
  buildSsrLoginCookieHeaders,
  isSsrLoginCookieForwardingEnabled,
  searchForSsr,
} from '@/modules/search/functions/ssrLoginCookies';
import { getServerSideGrowthBookContext } from '@/common/helper/getServerSideGrowthBookContext';
import Loading from '@/common/components/atom/loading';
import { ProfileNavigationPreview } from '@/modules/profile/components/ProfileNavigationPreview';
import { useBookingDwellPrefetch } from '@/modules/profile/hooks/useBookingDwellPrefetch';
import useLockScroll from '@/common/hooks/useLockScroll';
import { useSlowNavigationOverlay } from '@/common/hooks/useSlowNavigationOverlay';
import { useKeepAliveShown } from '@/common/hooks/useKeepAliveShown';
import Button from '@/common/components/atom/button';
import { getHost, HeaderBag } from '@/common/utils/getHost';

// Start loading the lazy parts of the first render as soon as this page's code runs (on a client
// navigation: before its first render), so the search box and the results list render in their
// first commit instead of a painted frame later. Same chunks this page renders anyway.
// docs/performance.md §۳.۲۱
if (typeof window !== 'undefined') {
  preloadSearchBox().catch(() => {});
  loadResultsCode();
}

const Search = ({ host, fragmentComponents, isMainSite, error, listingSeoRoutes }: any) => {
  const { isMobile } = useResponsive();
  const userInfo = useUserInfoStore(state => state.info);
  const userPending = useUserInfoStore(state => state.pending);
  const shouldUseSearchViewEventRoutesList = useFeatureValue<{ routes: Array<string | null> }>('search:use-front-end-search-view-event', {
    routes: [],
  });
  // City × specialty SEO experiment: only routes listed in this flag are treated (empty = off everywhere).
  const listingSeoExperiment = useFeatureValue<{ routes: Array<string | null> }>('search:listing-seo-experiment', { routes: [] });

  const {
    asPath,
    query: { params, lat, lon, ...queries },
  } = useRouter();
  const yaraSearchText = typeof queries.text === 'string' ? queries.text.trim() : '';

  const { handleChange, filters } = useFilterChange();
  const {
    isLanding,
    isLoading,
    total,
    seoInfo,
    selectedFilters,
    result,
    search,
    orderItems,
    selectedCategory,
    isConsult,
    isInPersonView,
    responseData,
  } = useSearch();
  const setUserSearchValue = useSearchStore(state => state.setUserSearchValue);
  const setGeoLocation = useSearchStore(state => state.setGeoLocation);
  const geoLocation = useSearchStore(state => state.geoLocation);
  const city = useSearchStore(state => state.city);
  const { changeRoute } = useSearchRouting();
  // A pause on a doctor's card starts that profile's booking card requests (docs/performance.md §۳.۱۸).
  const resultsRowRef = useRef<HTMLDivElement>(null);
  useBookingDwellPrefetch(resultsRowRef, isMobile && !error, asPath);
  const stat = useStat();
  const customize = useCustomize(state => state.customize);
  const showDesktopFiltersRow = useFeatureIsOn('search::desktop-filters-row');
  const showPlasmicSort = useFeatureIsOn('search_plasmic_sort');
  const showPlasmicConsultBanner = useFeatureIsOn('search_plasmic_consult_banner');
  const showDesktopSelectedFilters = useFeatureIsOn('search::desktop-selected-filters');
  const showPlasmicResult = useFeatureIsOn('search_plasmic_result');
  const showPlasmicSuggestion = useShowPlasmicSuggestion();


  useEffect(() => {
    if (typeof queries?.text === 'string') setUserSearchValue(queries.text);
    else setUserSearchValue('');
    if (lat && lon) setGeoLocation({ lat: +lat, lon: +lon });
  }, [queries?.text, lat, lon]);

  useEffect(() => {
    if ((params as string[])?.length === 1 && (params as string[])?.[0] === 'ir') {
      changeRoute({ params: { city: '' }, replace: true });
    }

    if (!isLoading && !selectedFilters.city && city.en_slug !== 'ir') {
      changeRoute({ params: { city: city?.en_slug }, query: { lat: null, lon: null }, replace: true });
    }
  }, [params, city, isLoading]);

  // ctr-false-drop-after-dr07 (ghane docs/bug-tickets): on SSR-hydrated loads this effect fires
  // twice for ONE search — commit 641616a0 changed useSearch's initial isLoading to
  // `searchRequest.isLoading` (false when the query is hydrated), so the mount flush passes the
  // guard and emits once with the initial `result`, then useSearch's own [asPath]/[search] effects
  // replace `result`'s identity ([] → [...search.result]) and re-fire the effect for a second,
  // byte-identical emission with the SAME query_id. It is race-conditional on `growthbook.ready`
  // being true at mount, which is why live traffic inflated ~11-17% (not +100%) and a 2-load
  // browser probe saw single emissions. Dedupe on (query_id + result_count): a genuinely new
  // search always has a fresh query_id, and pagination grows result.length, so both still emit.
  const lastSearchViewEmitKey = useRef<string | null>(null);

  // search_view / search_card_view / stat — must behave exactly as before keep-alive
  // (owner rule: analytics events never change). The body is unchanged; it is a named
  // function only so it can also run when the kept page is shown again (see below).
  const emitSearchView = () => {
    if (growthbook.ready && !userPending && !isLoading) {
      // Check if this is a listing page (e.g., /s/city/doctor/)
      const isListingPage = (params as string[])?.length === 2 && (params as string[])?.[1] === 'doctor';

      if (
        shouldUseSearchViewEventRoutesList.routes?.some(route => !!route && asPath.includes(route)) ||
        shouldUseSearchViewEventRoutesList.routes?.includes('*')
      ) {
        const emitKey = `${search.query_id}:${result.length}`;
        if (lastSearchViewEmitKey.current === emitKey) return;
        lastSearchViewEmitKey.current = emitKey;
        if (!fragmentComponents?.showPlasmicResult || showPlasmicResult) {
          splunkInstance('search').sendEvent({
            group: 'search_metrics',
            type: 'search_view',
            event: {
              filters: selectedFilters,
              result_count: result.length,
              location: city.en_slug,
              ...(geoLocation ?? null),
              city_id: city.id,
              query_id: search.query_id,
              user_id: userInfo?.id ?? null,
              user_type: hasDoctorIdentity(userInfo) ? 'doctor' : 'normal-user',
              ...(!!search?.semantic_search && { semantic_search: search?.semantic_search }),
              ...(responseData?.query_attributes && { query_attributes: responseData.query_attributes }),
              ...(responseData?.personalization && { personalization: responseData.personalization }),
              url: {
                href: window.location.href,
                qurey: { ...queries },
                pathname: window.location.pathname,
                host: window.location.host,
              },
            },
          });
        }

        // Only send search_card_view event if it's NOT a listing page
        if (!isListingPage) {
          splunkInstance('search').sendBatchEvent({
            group: 'search_metrics',
            type: 'search_card_view',
            events: result.map(item => ({
              card_data: {
                action: item.actions?.map?.((item: any) =>
                  JSON.stringify({ outline: item.outline, title: item.title, top_title: removeHtmlTagInString(item.top_title) }),
                ),
                _id: item._id,
                position: item.position,
                server_id: item.server_id,
                title: item.title,
                type: item.type,
                url: item.url,
                rates_count: item.rates_count,
                satisfaction: item.satisfaction,
              },
              filters: selectedFilters,
              result_count: result.length,
              location: city.en_slug,
              ...(geoLocation ?? null),
              city_id: city.id,
              query_id: search.query_id,
              user_id: userInfo?.id ?? null,
              user_type: hasDoctorIdentity(userInfo) ? 'doctor' : 'normal-user',
              url: {
                href: window.location.href,
                qurey: { ...queries },
                pathname: window.location.pathname,
                host: window.location.host,
              },
            })),
          });
        }

        return;
      }
      stat.mutate({
        route: asPath,
        filters: selectedFilters,
        result: result,
        ...(!isLanding && { centerTypeFilterPresence: 1 }),
      });
    }
  };

  useEffect(() => {
    emitSearchView();
    // Same trigger as before; emitSearchView reads this render's values like the old inline body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, userPending, isLoading, growthbook.ready]);

  // Keep-alive: Back no longer remounts this page, but before keep-alive every Back remounted
  // it — resetting the dedupe ref and emitting search_view / search_card_view / stat again.
  // Reproduce exactly that. docs/performance.md §۳.۵
  useKeepAliveShown(() => {
    lastSearchViewEmitKey.current = null;
    emitSearchView();
  });

  const handleNextPage = useCallback(() => {
    const currentPage = (queries?.page as string) ? (queries?.page as string) : 1;
    changeRoute({
      query: {
        page: +currentPage + 1,
      },
      scroll: false,
    });
  }, [queries?.page, changeRoute]);

  const memoizedFilters = useMemo(() => filters, [filters.sortBy, filters.freeturn]);

  const handleChangeMemoized = useCallback(
    (key: string, value: any) => {
      handleChange(key, value);
    },
    [handleChange],
  );

  const experimentSeo = useMemo(
    () =>
      buildListingSeoExperiment({
        asPath,
        // SSR reads the list from getServerSideProps: the client GrowthBook singleton is not loaded
        // on the server, so the hook alone only changed the head after hydration (invisible to crawlers).
        routes: listingSeoRoutes ?? listingSeoExperiment?.routes ?? [],
        crumbs: (seoInfo as any)?.jsonld,
        total,
        result: result as any,
      }),
    [asPath, listingSeoRoutes, listingSeoExperiment, seoInfo, total, result],
  );

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[60vh] space-y-4 p-5">
        <Text fontSize="lg" fontWeight="bold" className="text-center">
          {error.message || 'خطا در دریافت نتایج جستجو'}
        </Text>
        <Button onClick={() => window.location.reload()}>تلاش مجدد</Button>
      </div>
    );
  }

  return (
    <>
      <Fragment name="LocationSelectionScript" />
      {experimentSeo ? (
        <Seo
          {...seoInfo}
          title={experimentSeo.title}
          description={experimentSeo.description}
          canonicalUrl={seoInfo?.canonical_link}
          jsonlds={experimentSeo.jsonlds}
          host={host}
        />
      ) : (
        <Seo
          {...seoInfo}
          canonicalUrl={seoInfo?.canonical_link}
          jsonlds={listingJsonLds((seoInfo as any)?.jsonld)}
          host={host}
        />
      )}
      <div className={`flex flex-col items-center justify-center bg-white ${isMobile ? 'sticky top-0 z-20' : ''}`}>
        {/* Remount only when the text or the city changes — the box reads its text as a default value at mount.
            Keyed on the whole path it was rebuilt (a full Plasmic mount, ~0.8s at CPU x4) on every filter,
            category and page change (docs/performance.md §۳.۱۴). */}
        <Suggestion
          showPlasmicSuggestion={showPlasmicSuggestion}
          key={`${typeof queries.text === 'string' ? queries.text : ''}|${city?.id}`}
          overlay
        />
        {showDesktopFiltersRow ? <MobileToolbar /> : <MobileRowFilter />}
      </div>
      <div className="container flex flex-col p-3 md:!pt-5 mx-auto space-y-3 md:p-0">
        {/* A click on a card, button or link in the results — either renderer —
            means the patient is acting on plain search, so the Yara offer must not
            appear. Captured on the row itself (a wrapper would break `space-s-5`);
            the filter column opts out with `data-yara-ignore`. */}
        <div
          ref={resultsRowRef}
          className={classNames('flex flex-col md:space-y-0 md:flex-row md:space-s-5', { 'space-y-3': !showDesktopSelectedFilters })}
          onClickCapture={e => {
            if (isResultClick(e.target)) markSearchResultClick(yaraSearchText);
          }}
        >
          {!isLanding && <Filter isLoading={isLoading} />}
          {fragmentComponents?.showPlasmicResult || showPlasmicResult ? (
            <SearchGlobalContextsProvider>
              <Fragment
                name="ResultView"
                props={{
                  isLanding: isLanding,
                  //banner
                  categoryValue: selectedCategory?.value,
                  categoryTitle: selectedCategory?.title,
                  isHideConsultBanner: isConsult || isInPersonView,
                  // sort
                  orderItems: orderItems,
                  selectedSort: memoizedFilters['sortBy'],
                  selectedTurn: memoizedFilters['freeturn'],
                  isLoadingResult: isLoading,
                  onChangeFreeTurn: (value: any) => handleChange('freeturn', value),
                  onChangeSort: (value: any) => handleChange('sortBy', value),
                  totalResult2: addCommas(total),
                  // result
                  searchResultResponse: {
                    ...responseData,
                    search: {
                      ...search,
                      result,
                    },
                  },
                  nextPageTrigger: handleNextPage,
                  imageSrcPrefix: publicRuntimeConfig.CDN_BASE_URL,
                  location: {
                    city_name: city?.en_slug,
                    city_id: city?.id,
                    ...geoLocation,
                  },
                  paginationLoadingStatus: isLoading,
                }}
              />
            </SearchGlobalContextsProvider>
          ) : (
            <div className="flex flex-col w-full">
              {!isLanding && !isMobile && (
                <div className="items-center justify-between hidden mb-3 md:flex">
                  {fragmentComponents?.showPlasmicSort || showPlasmicSort ? (
                    <Fragment
                      name="Sort"
                      props={{
                        orderItems: orderItems,
                        selectedSort: memoizedFilters['sortBy'],
                        selectedTurn: memoizedFilters['freeturn'],
                        total: addCommas(total),
                        isLoading: isLoading,
                        onChangeFreeTurn: (value: any) => handleChange('freeturn', value),
                        onChangeSort: (value: any) => handleChange('sortBy', value),
                      }}
                    />
                  ) : (
                    <>
                      <Sort />
                      {isLoading ? (
                        <Skeleton w="5rem" h="1rem" rounded="full" />
                      ) : (
                        <Text fontSize="sm" fontWeight="semiBold">
                          {addCommas(total)} نتیجه
                        </Text>
                      )}
                    </>
                  )}
                </div>
              )}
              {customize.showConsultServices && (showPlasmicConsultBanner || fragmentComponents?.showPlasmicConsultBanner) && (
                <div className="mb-3 md:hidden">
                  <Fragment
                    name="ConsultBanner"
                    props={{
                      categoryValue: selectedCategory?.value,
                      categoryTitle: selectedCategory?.title,
                      isHide: isConsult || isInPersonView,
                    }}
                  />
                </div>
              )}
              {/* The non-Plasmic consult banner is rendered by Result (same place in the DOM) so it can
                  appear together with the list instead of reshaping above the loading skeleton
                  (docs/performance.md §۳.۲۱). It is server-rendered (DR-08 §۱۸), no reserved min-height. */}
              <Result
                showConsultBanner={customize.showConsultServices && !(showPlasmicConsultBanner || fragmentComponents?.showPlasmicConsultBanner)}
              />
            </div>
          )}
        </div>
        <div>
          <SearchSeoBox />
        </div>
      </div>
      <UnknownCity />
      {yaraSearchText && <YaraSymptomInvite text={yaraSearchText} />}
      <ProfileNavigationOverlay />
      <ProfileNavigationPreview />
    </>
  );
};

/**
 * Full-screen loader while leaving search for a doctor/center page — only when the navigation
 * is slow (useSlowNavigationOverlay); fast ones show just the top progress bar. On mobile a tap on
 * a doctor shows ProfileNavigationPreview instead (the profile's own first frame), so there the
 * loader is desktop-only for /dr. Owns its own state on
 * purpose: this state used to live at the page root, so every route start/complete
 * re-rendered the entire search tree — and with keep-alive (docs/performance.md §۳.۵) the
 * page stays mounted, so that meant two full re-renders of a hidden page per navigation.
 */
const isProfileUrl = (url: string) => url.startsWith('/dr') || url.startsWith('/center');

/** Mobile taps on a doctor get ProfileNavigationPreview; the loader stays for centers and desktop. */
const overlayForUrl = (url: string) =>
  isProfileUrl(url) && !(url.startsWith('/dr') && typeof window !== 'undefined' && !window.matchMedia?.('(min-width: 1024px)').matches);

const ProfileNavigationOverlay = () => {
  const isPageLoading = useSlowNavigationOverlay(overlayForUrl);

  if (!isPageLoading) return null;
  return (
    <div className="fixed top-0 left-0 w-full h-full bg-white bg-opacity-80 flex justify-center items-center z-50">
      <Loading width={50} />
    </div>
  );
};

Search.getLayout = function getLayout(page: ReactElement) {
  return (
    <LayoutWithHeaderAndFooter {...page.props.config} shouldShowPromoteApp={false}>
      {page}
    </LayoutWithHeaderAndFooter>
  );
};

 const _getServerSideProps: GetServerSideProps = withCSR(
  withServerUtils(async (context: GetServerSidePropsContext, themeConfing: ThemeConfig) => {
    const { params, ...query } = context.query;

    if (params?.length === 1 && (params as string[])?.[0] === 'ir') {
      return {
        redirect: {
          destination: '/s',
          permanent: true,
        },
      };
    }
    let showPlasmicSuggestion: boolean = false;
    let showPlasmicResult: boolean = false;
    let showPlasmicSort: boolean = false;
    let showPlasmicConsultBanner: boolean = false;
    let listingSeoRoutes: Array<string | null> = [];

    const host = getHost(context.req.headers as unknown as HeaderBag);
    const path = context.resolvedUrl;
    const url = `https://${host}${path}`;
    const isMainSite = host === 'www.paziresh24.com' || host === 'p24-patient.darkube.app';
    try {
      const growthbookContext = getServerSideGrowthBookContext(context.req as NextApiRequest);
      const growthbook = new GrowthBook(growthbookContext);
      growthbook.setAttributes({ url });
      await growthbook.loadFeatures({ timeout: 500 });
      // Plasmic
      showPlasmicSuggestion = growthbook.isOn('search_plasmic_suggestion');
      showPlasmicResult = growthbook.isOn('search_plasmic_result');
      showPlasmicSort = growthbook.isOn('search_plasmic_sort');
      showPlasmicConsultBanner = growthbook.isOn('search_plasmic_consult_banner');
      listingSeoRoutes = growthbook.getFeatureValue<{ routes: Array<string | null> }>('search:listing-seo-experiment', { routes: [] })?.routes ?? [];
    } catch (error) {
      console.error(error);
    }

    try {
      const queryClient = new QueryClient();

      const university = themeConfing?.partnerKey;

      // DR-02: with the flag on, a logged-in visitor of www gets their own list in the first paint.
      // ⚠️ Order matters: the HTML of www/s/* is shared by the CDN, so the flag may only be turned on
      // AFTER the CDN rule for /s/* keys on the login cookies — otherwise one visitor's list is served
      // to others. Off (the default) ⇒ null ⇒ the call below is the same anonymous call as before.
      const loginCookieHeaders = buildSsrLoginCookieHeaders({
        enabled: isSsrLoginCookieForwardingEnabled(serverRuntimeConfig?.SEARCH_SSR_FORWARD_LOGIN_COOKIES),
        host: context.req.headers.host,
        brandHost: context.req.headers['x-brand-identity'],
        cookieHeader: context.req.headers.cookie,
      });

      const searchData = await queryClient.fetchQuery(
        [
          ServerStateKeysEnum.Search,
          {
            route: (params as string[])?.join('/') ?? '',
            query: {
              ...query,
              ...(university && { university }),
            },
          },
        ],
        () =>
          searchForSsr(
            {
              route: (params as string[])?.join('/') ?? '',
              query: {
                ...query,
                ...(university && { university }),
              },
            },
            loginCookieHeaders,
          ),
      );

      // No search backend answered: still render for people, but answer crawlers/CDN with 503.
      if (isSearchDegraded(searchData)) {
        applySearchUnavailableStatus(context.res);
      }

      // DR-07 (ghane docs/dev-requests/dr-07-online-visit-suggestion-to-backend.md §13 condition 1):
      // the "should we suggest an online-visit doctor" decision + the second consult search that used
      // to live here are now made entirely server-side (search-service) and arrive as the
      // `promoted_online_visit` sibling key inside `searchData` itself — no second SSR fetch needed.

      return {
        props: {
          dehydratedState: dehydrate(queryClient),
          fragmentComponents: {
            showPlasmicSuggestion: isMainSite || showPlasmicSuggestion,
            showPlasmicResult,
            showPlasmicSort,
            showPlasmicConsultBanner,
            url,
          },
          isMainSite,
          listingSeoRoutes,
        },
      };
    } catch (error) {
      console.dir(error);
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404)
          return {
            notFound: true,
          };
        if (error.response?.status === 429) {
          // The page shows a "too many requests" message; without this it went out as a 200 page.
          applySearchUnavailableStatus(context.res);
          return {
            props: {
              error: {
                statusCode: 429,
                message: 'تعداد درخواست‌های شما بیش از حد مجاز است. لطفاً کمی صبر کنید و دوباره تلاش کنید.',
              },
            },
          };
        }
      }
      throw new TypeError(JSON.stringify(error));
    }
  }),
);

export default Search;



// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/s/[[...params]]',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  